import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { AuthService } from '../../../core/services/auth.service';
import { CommitteeEvent, EventService } from '../../../core/services/event.service';

interface OutstandingEvent {
  remaining: number;
  overdue: boolean;
}

/**
 * Self-service nudge for a logged-in member: highlights unpaid/partial
 * contributions across their events (excluding ones they're exempted from),
 * on the pages a member actually looks at (dashboard, events list). No-ops
 * entirely for admins. Mirrors `MemberDuesAlertComponent`'s shape, walking
 * events instead of months.
 */
@Component({
  selector: 'app-event-dues-alert',
  standalone: true,
  imports: [CommonModule, RouterLink],
  template: `
    <ng-container *ngIf="kind() as k">
      <div [ngClass]="bannerClass()"
           class="rounded-lg border px-4 py-3 flex items-start gap-3 text-sm">
        <svg class="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        <div class="flex-1">
          <div class="font-medium">{{ titleText() }}</div>
          <p class="mt-0.5 opacity-90">{{ bodyText() }}</p>
          <div class="flex flex-wrap gap-2 mt-2">
            <a routerLink="/events"
               class="px-3 py-1 text-xs font-medium bg-white/80 rounded hover:bg-white">
              View events
            </a>
          </div>
        </div>
      </div>
    </ng-container>
  `
})
export class EventDuesAlertComponent implements OnInit {
  private auth = inject(AuthService);
  private eventService = inject(EventService);

  readonly isAdmin = computed(() => this.auth.isSuperAdminSignal() || this.auth.isTenantAdminSignal());

  private readonly outstandingEvents = signal<OutstandingEvent[]>([]);

  readonly kind = computed<'arrears' | 'partial' | null>(() => {
    if (this.isAdmin()) return null;
    const events = this.outstandingEvents();
    if (events.length >= 2) return 'arrears';
    if (events.length === 1) return 'partial';
    return null;
  });

  readonly totalOutstanding = computed(() =>
    this.outstandingEvents().reduce((sum, e) => sum + e.remaining, 0)
  );

  readonly overdueCount = computed(() =>
    this.outstandingEvents().filter(e => e.overdue).length
  );

  // Due date only affects tone/wording, never whether the banner shows at all —
  // any outstanding event forces red once at least one of them is overdue.
  readonly bannerClass = computed(() => {
    if (this.overdueCount() > 0) return 'bg-red-50 border-red-300 text-red-800';
    switch (this.kind()) {
      case 'arrears': return 'bg-red-50 border-red-300 text-red-800';
      case 'partial': return 'bg-amber-50 border-amber-300 text-amber-800';
      default:        return '';
    }
  });

  readonly titleText = computed(() => {
    switch (this.kind()) {
      case 'arrears': return `Pending contributions across ${this.outstandingEvents().length} events`;
      case 'partial': return 'Pending event contribution';
      default:        return '';
    }
  });

  readonly bodyText = computed(() => {
    const overdue = this.overdueCount();
    const overdueClause = overdue > 0 ? ` (${overdue} overdue)` : '';
    switch (this.kind()) {
      case 'arrears':
        return `Rs. ${this.totalOutstanding().toLocaleString()} is due in total${overdueClause}. Please settle your event contributions as soon as possible.`;
      case 'partial':
        return `You have an event with an outstanding contribution${overdueClause} — Rs. ${this.totalOutstanding().toLocaleString()} due.`;
      default:
        return '';
    }
  });

  ngOnInit(): void {
    if (this.isAdmin()) return;

    this.eventService.getEvents({ page: 1, pageSize: 100 }).subscribe({
      next: (res) => {
        if (!res?.success || !res.data) return;
        const events: CommitteeEvent[] = res.data.data ?? [];
        const now = Date.now();
        const outstanding = events
          .filter(e => e.myStatus != null && e.myStatus !== 'Excused')
          .map(e => ({
            remaining: Math.max((e.myExpected ?? 0) - (e.myPaid ?? 0), 0),
            overdue: !!e.paymentDueDate && new Date(e.paymentDueDate).getTime() < now
          }))
          .filter(e => e.remaining > 0);
        this.outstandingEvents.set(outstanding);
      }
    });
  }
}
